import { useMsal } from "@azure/msal-react";
import { IconButton, Pivot, PivotItem } from "@fluentui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ChatAppResponse, getHeaders } from "../../api";
import { getToken, useLogin } from "../../authConfig";
import { DocxViewer } from "../DocxViewer";
import { MarkdownViewer } from "../MarkdownViewer";
import { SupportingContent } from "../SupportingContent";
import styles from "./AnalysisPanel.module.css";
import { AnalysisPanelTabs } from "./AnalysisPanelTabs";
import { ThoughtProcess } from "./ThoughtProcess";

interface Props {
    className: string;
    activeTab: AnalysisPanelTabs;
    onActiveTabChanged: (tab: AnalysisPanelTabs) => void;
    onClose?: () => void;
    activeCitation: string | undefined;
    citationHeight: string;
    answer: ChatAppResponse;
    onCitationClicked?: (citationFilePath: string) => void;
    showDevTools?: boolean;
}


export const AnalysisPanel = ({ answer, activeTab, activeCitation, citationHeight, className, onActiveTabChanged, onClose, onCitationClicked, showDevTools }: Props) => {
    const hasThoughts: boolean = showDevTools === true && !!(answer.context.thoughts?.length);
    const dataPoints = answer.context.data_points;
    const hasSupportingContent = Boolean(
        dataPoints &&
            ((dataPoints.text && dataPoints.text.length > 0) ||
                (dataPoints.images && dataPoints.images.length > 0) ||
                (dataPoints.external_results_metadata && dataPoints.external_results_metadata.length > 0))
    );
    const [citation, setCitation] = useState("");

    const client = useLogin ? useMsal().instance : undefined;
    const { t } = useTranslation();

    // Re-fetch whenever activeCitation changes so a second/third citation click while
    // the panel is already open swaps the displayed content. The previous useEffect
    // had `[]` deps so it ran only once on mount and subsequent clicks were silently
    // ignored.
    useEffect(() => {
        let cancelled = false;
        let createdObjectUrl: string | null = null;

        const fetchCitation = async () => {
            if (!activeCitation) {
                setCitation("");
                return;
            }
            const token = client ? await getToken(client) : undefined;
            const originalHash = activeCitation.indexOf("#") !== -1 ? activeCitation.split("#")[1] : "";
            const response = await fetch(activeCitation, {
                method: "GET",
                headers: await getHeaders(token)
            });
            const citationContent = await response.blob();
            let citationObjectUrl = URL.createObjectURL(citationContent);
            createdObjectUrl = citationObjectUrl;
            if (originalHash) {
                citationObjectUrl += "#" + originalHash;
            }
            // If activeCitation changed again while we were fetching, drop this result.
            if (cancelled) {
                URL.revokeObjectURL(createdObjectUrl);
                return;
            }
            setCitation(citationObjectUrl);
        };

        fetchCitation();

        // Cleanup: when activeCitation changes or component unmounts, revoke the
        // previously-created blob URL to avoid leaking memory on every click.
        return () => {
            cancelled = true;
            if (createdObjectUrl) {
                URL.revokeObjectURL(createdObjectUrl);
            }
        };
    }, [activeCitation]);

    const renderFileViewer = () => {
        if (!activeCitation) {
            return null;
        }

        const fileExtension = activeCitation.split(".").pop()?.toLowerCase();
        switch (fileExtension) {
            case "png":
                return <img src={citation} className={styles.citationImg} alt="Citation Image" />;
            case "md":
                return <MarkdownViewer src={activeCitation} />;
            case "doc":
            case "docx":
                // Browsers can't natively render Word in an <iframe> — they trigger a
                // download instead. DocxViewer fetches the file and converts it to HTML
                // via mammoth.js for inline preview.
                return <DocxViewer src={activeCitation} />;
            default:
                return <iframe title="Citation" src={citation} width="100%" height={citationHeight} />;
        }
    };

    return (
        <div className={`${className} ${styles.analysisPanelWrapper}`}>
            {onClose && (
                <IconButton
                    className={styles.closeButton}
                    iconProps={{ iconName: "Cancel" }}
                    onClick={onClose}
                    title={t("labels.closeButton")}
                    ariaLabel={t("labels.closeButton")}
                />
            )}
            <Pivot
                selectedKey={activeTab}
                onLinkClick={pivotItem => pivotItem && onActiveTabChanged(pivotItem.props.itemKey! as AnalysisPanelTabs)}
            >
                {hasThoughts && (
                    <PivotItem itemKey={AnalysisPanelTabs.ThoughtProcessTab} headerText={t("headerTexts.thoughtProcess")}>
                        <ThoughtProcess
                            thoughts={answer.context.thoughts || []}
                            onCitationClicked={onCitationClicked}
                            conversationId={typeof answer.session_state === "string" ? answer.session_state : null}
                        />
                    </PivotItem>
                )}
                {hasSupportingContent && (
                    <PivotItem itemKey={AnalysisPanelTabs.SupportingContentTab} headerText={t("headerTexts.supportingContent")}>
                        <SupportingContent supportingContent={answer.context.data_points} />
                    </PivotItem>
                )}
                {activeCitation && (
                    <PivotItem itemKey={AnalysisPanelTabs.CitationTab} headerText={t("headerTexts.citation")}>
                        {renderFileViewer()}
                    </PivotItem>
                )}
            </Pivot>
        </div>
    );
};
